/**
 * AccessDenied Component
 *
 * Reusable component to display when user doesn't have permission to access a page
 *
 * @example Basic usage
 * <AccessDenied />
 *
 * @example Custom message
 * <AccessDenied
 *   title="Module Access Required"
 *   message="Contact your administrator to request access to the Zone Renewal module."
 * />
 *
 * @example With home button
 * <AccessDenied
 *   showBackButton={false}
 *   showHomeButton={true}
 * />
 *
 * @example Custom contact info
 * <AccessDenied
 *   contactInfo="Email: admin@example.com"
 * />
 */

import React from 'react';
import PropTypes from 'prop-types';
import * as Icon from 'react-feather';
import { Button, Card, CardBody, CardTitle } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import BreadCrumbs from '../../layouts/breadcrumbs/BreadCrumbs';

const AccessDenied = ({
  title = 'Access Denied',
  message = 'You do not have permission to access this page',
  showBackButton = true,
  showHomeButton = false,
  homeLink = '/dashboard',
  contactInfo = 'Contact your administrator if you believe this is an error.',
  icon = 'AlertCircle',
  iconSize = 48,
  iconColor = 'danger'
}) => {
  const navigate = useNavigate();

  const IconComponent = Icon[icon] || Icon.AlertCircle;

  return (
    <div>
      <BreadCrumbs />
      <Card>
        <CardTitle>
          <h4 className="card-header">{title}</h4>
        </CardTitle>
        <CardBody className="p-3">
          <div className="text-center py-5">
            <IconComponent size={iconSize} className={`text-${iconColor} mb-3`} />
            <h4>{message}</h4>
            {contactInfo && <p className="text-muted">{contactInfo}</p>}
            <div className="mt-4">
              {showBackButton && (
                <Button color="secondary" className="me-2" onClick={() => navigate(-1)}>
                  <Icon.ArrowLeft size={14} className="me-1" /> Go Back
                </Button>
              )}
              {showHomeButton && (
                <Button color="primary" onClick={() => navigate(homeLink)}>
                  <Icon.Home size={14} className="me-1" /> Go to Home
                </Button>
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

AccessDenied.propTypes = {
  title: PropTypes.string,
  message: PropTypes.string,
  showBackButton: PropTypes.bool,
  showHomeButton: PropTypes.bool,
  homeLink: PropTypes.string,
  contactInfo: PropTypes.string,
  icon: PropTypes.string,
  iconSize: PropTypes.number,
  iconColor: PropTypes.string,
};

export default AccessDenied;
